import { type FC, useEffect, Component, type ReactNode } from 'react';
import { useAppStore } from '@/stores/app-store';
import { Header } from '@/components/layout/Header';
import { Dashboard } from '@/components/layout/Dashboard';
import { BootSequence } from '@/components/ui/BootSequence';

// ---------------------------------------------------------------------------
// Error Boundary
// ---------------------------------------------------------------------------

interface EBProps { children: ReactNode }
interface EBState { hasError: boolean; error?: Error }

class ErrorBoundary extends Component<EBProps, EBState> {
  constructor(props: EBProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): EBState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[DetectX] Uncaught error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-surface text-slate-300">
          <div className="glass-panel p-8 max-w-md text-center space-y-3">
            <h2 className="text-lg font-semibold text-threat-critical">Something went wrong</h2>
            <p className="text-sm text-slate-400">{this.state.error?.message ?? 'An unexpected error occurred.'}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-4 py-1.5 rounded bg-brand/20 text-brand-light text-sm hover:bg-brand/30 transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

const AppInner: FC = () => {
  const setCommandPaletteOpen = useAppStore((s) => s.setCommandPaletteOpen);

  // Cmd+K command palette shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setCommandPaletteOpen]);

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-surface">
      <Header />
      <Dashboard />
      <BootSequence />
    </div>
  );
};

export const App: FC = () => (
  <ErrorBoundary>
    <AppInner />
  </ErrorBoundary>
);
